// Address search service using Google Maps Places API and Geocoding API

import apiService from './apiService';

class AddressSearchService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  // Parse coordinates from user input (lat,lon format)
  parseCoordinates(input) {
    const cleaned = input.trim();
    
    // Match patterns like: "40.7128, -74.0060" or "40.7128,-74.0060"
    const coordPattern = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/;
    const match = cleaned.match(coordPattern);
    
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      
      // Validate coordinate ranges
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return {
          coordinates: { lat, lng },
          address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          isDirectCoordinates: true
        };
      }
    }
    
    return null;
  }

  // Search for addresses using Google Places API (autocomplete suggestions)
  async searchPlaces(query, sessionToken = null) {
    if (!query || query.trim().length < 3) {
      return [];
    }

    const cacheKey = `places:${query.toLowerCase()}`;
    
    // Check cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) { // 5 minute cache
        return cached.results;
      }
    }

    // Check for pending request
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const request = this.performPlacesSearch(query, sessionToken, cacheKey);
    this.pendingRequests.set(cacheKey, request);

    return request;
  }

  async performPlacesSearch(query, sessionToken, cacheKey) {
    try {
      // Use real Google Places API via backend
      const response = await apiService.searchPlaces(query, sessionToken);
      
      // Clean up pending request
      this.pendingRequests.delete(cacheKey);

      // Transform Google Places API response
      const results = response.suggestions.map(suggestion => {
        if (suggestion.placePrediction) {
          const prediction = suggestion.placePrediction;
          return {
            place_id: prediction.placeId,
            description: prediction.text.text,
            formatted_address: prediction.text.text, // Autocomplete doesn't have formattedAddress
            geometry: null, // Autocomplete doesn't return coordinates - need Place Details for that
            types: prediction.types || [],
            structured_formatting: prediction.structuredFormat ? {
              main_text: prediction.structuredFormat.mainText.text,
              secondary_text: prediction.structuredFormat.secondaryText?.text || ''
            } : null
          };
        }
        return null;
      }).filter(Boolean);

      // Cache results
      this.cache.set(cacheKey, {
        results,
        timestamp: Date.now()
      });

      return results;
    } catch (error) {
      this.pendingRequests.delete(cacheKey);
      console.error('Places API error:', error);
      
      // Fallback to mock results if API fails
      console.warn('Falling back to mock results due to Places API error');
      const mockResults = this.generateMockResults(query);
      
      // Cache mock results with shorter TTL
      this.cache.set(cacheKey, {
        results: mockResults,
        timestamp: Date.now()
      });
      
      return mockResults;
    }
  }

  // Geocode address using our backend API (fallback method)
  async geocodeAddress(address) {
    try {
      // First check if it's direct coordinates
      const coords = this.parseCoordinates(address);
      if (coords) {
        return [{
          place_id: 'direct-coords',
          description: coords.address,
          formatted_address: coords.address,
          geometry: {
            location: coords.coordinates
          },
          types: ['point_of_interest'],
          isDirectCoordinates: true
        }];
      }

      // TODO: Real Google Places API Integration
      // For full functionality, integrate Google Places API:
      // 1. Add GOOGLE_PLACES_API_KEY to backend environment
      // 2. Create backend endpoint for Places API Text Search or Geocoding with multiple results
      // 3. Replace this simulation with real API call
      
      // For now, simulate realistic search results for demo purposes
      const mockResults = this.generateMockResults(address);
      return mockResults;
    } catch (error) {
      console.error('Geocoding error:', error);
      return [];
    }
  }

  // Generate mock results for demo/testing (remove when Places API is integrated)
  generateMockResults(address) {
    const lowerAddress = address.toLowerCase();
    
    // Simulate different types of results based on common search patterns
    if (lowerAddress.includes('new york') || lowerAddress.includes('nyc')) {
      return [
        {
          place_id: 'mock-1',
          description: 'New York, NY, USA',
          formatted_address: 'New York, NY, USA',
          geometry: { location: { lat: 40.7128, lng: -74.0060 } },
          types: ['locality', 'political']
        },
        {
          place_id: 'mock-2', 
          description: 'Times Square, New York, NY, USA',
          formatted_address: 'Times Square, New York, NY 10036, USA',
          geometry: { location: { lat: 40.7580, lng: -73.9855 } },
          types: ['point_of_interest', 'establishment']
        }
      ];
    }
    
    if (lowerAddress.includes('san francisco') || lowerAddress.includes('sf')) {
      return [
        {
          place_id: 'mock-3',
          description: 'San Francisco, CA, USA',
          formatted_address: 'San Francisco, CA, USA',
          geometry: { location: { lat: 37.7749, lng: -122.4194 } },
          types: ['locality', 'political']
        },
        {
          place_id: 'mock-4',
          description: 'Golden Gate Bridge, San Francisco, CA, USA',
          formatted_address: 'Golden Gate Bridge, San Francisco, CA, USA',
          geometry: { location: { lat: 37.8199, lng: -122.4783 } },
          types: ['point_of_interest', 'establishment']
        }
      ];
    }

    // Default mock result for any other search
    return [
      {
        place_id: 'mock-default',
        description: `${address} (Mock Result)`,
        formatted_address: `${address} - Demo Location`,
        geometry: { location: { lat: 37.7749 + Math.random() * 0.1, lng: -122.4194 + Math.random() * 0.1 } },
        types: ['geocoded'],
        note: 'Mock result - integrate Google Places API for real functionality'
      }
    ];
  }

  // Get place details by place_id (for Places API integration)
  async getPlaceDetails(placeId, sessionToken = null) {
    if (placeId === 'direct-coords' || placeId.startsWith('mock-')) {
      // Handle direct coordinates case or mock results
      return null; // Already have coordinates
    }

    try {
      // Use real Google Places API for place details
      const response = await apiService.getPlaceDetails(placeId, sessionToken);
      
      if (response && response.place) {
        return {
          place_id: response.place.id,
          formatted_address: response.place.formattedAddress,
          geometry: response.place.location ? {
            location: {
              lat: response.place.location.latitude,
              lng: response.place.location.longitude
            }
          } : null,
          name: response.place.displayName?.text,
          types: response.place.types || []
        };
      }
      
      return null;
    } catch (error) {
      console.error('Place details error:', error);
      return null;
    }
  }

  // Format search result for display
  formatSearchResult(result) {
    return {
      id: result.place_id,
      title: result.description || result.formatted_address,
      subtitle: result.formatted_address,
      coordinates: result.geometry?.location,
      types: result.types || [],
      isDirectCoordinates: result.isDirectCoordinates || false
    };
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Generate session token for Places API (helps with billing)
  generateSessionToken() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}

// Export singleton instance
export const addressSearchService = new AddressSearchService();
export default addressSearchService;